import React, { forwardRef, type CSSProperties, type ReactNode } from "react";

/**
 * Plain span-based text for layouts and copy.
 *
 * `@react-spectrum/s2` `Text` is slotted (React Aria): inside Button, fields, etc. it
 * requires `slot="description" | "errorMessage"`. Using it as generic typography throws
 * "A slot prop is required". This component matches typical v3 `Text` usage (UNSAFE_style).
 */
export interface SpectrumCompatTextProps extends Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  "slot"
> {
  children?: ReactNode;
  UNSAFE_style?: CSSProperties;
  UNSAFE_className?: string;
  /** Ignored — present for compatibility if S2-style props are passed through. */
  styles?: string;
  isHidden?: boolean;
  /** Ignored — S2 field slots are not used by this span-based Text. */
  slot?: string | null;
}

const Text = forwardRef<HTMLSpanElement, SpectrumCompatTextProps>(
  (
    {
      children,
      UNSAFE_style,
      UNSAFE_className,
      className,
      style,
      isHidden,
      slot: _slot,
      styles: _styles,
      ...rest
    },
    ref,
  ) => {
    if (isHidden) {
      return null;
    }
    const merged = { ...UNSAFE_style, ...style };
    const hasStyle = Object.keys(merged).some(
      (key) => merged[key as keyof CSSProperties] !== undefined,
    );
    return (
      <span
        ref={ref}
        className={
          [UNSAFE_className, className].filter(Boolean).join(" ") || undefined
        }
        {...(hasStyle ? { style: merged } : {})}
        {...rest}
      >
        {children}
      </span>
    );
  },
);

Text.displayName = "Text";

export default Text;
